// Режим «от руки» — тетрадка в клетку. Ноль зависимостей: мегабайт доски за
// чертёж от руки платить незачем, и в колонке она всё равно не помещается.
//
// Написал и стёр: нарисованное — не объекты, подвинуть штрих нельзя, только
// отменить или стереть. Отрезок — moveTo/lineTo, овал — ctx.ellipse, отмена —
// массив. Единственное неочевидное место — сглаживание штриха по серединам
// точек: без него мышь даёт ломаную и цифры в столбик нечитаемы.
//
// Лист постоянного размера (см. Sheet): координаты штрихов абсолютны, поэтому
// сворачивание и разворачивание не двигают нарисованное.

import { useEffect, useRef, useState } from "react";
import { plural } from "../../lib/dates";
import type { NotebookShape, ScratchPoint } from "../../lib/task-draft";
import { Sheet } from "./Sheet";
import { ScratchIcon } from "./ScratchIcon";

type Point = ScratchPoint;
type Tool = "pen" | "line" | "ellipse" | "text" | "erase";
type Shape = NotebookShape;

const SHEET_W = 1500;
const SHEET_H = 1000;
const PEN = 2.4;
const ERASER = 18;
const CELL = 22;
const TEXT_PX = 20;

// Подписи не рисуются, но остаются источником доступных имён и подсказок.
const TOOLS: [Tool, string][] = [
  ["pen", "перо"],
  ["line", "линия"],
  ["ellipse", "овал"],
  ["text", "текст"],
  ["erase", "ластик"],
];

function ink(): string {
  return getComputedStyle(document.documentElement).getPropertyValue("--ink").trim() || "#191714";
}

function draw(ctx: CanvasRenderingContext2D, shapes: Shape[], color: string): void {
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const shape of shapes) {
    ctx.globalCompositeOperation = shape.tool === "erase" ? "destination-out" : "source-over";
    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    if (shape.tool === "pen" || shape.tool === "erase") {
      ctx.lineWidth = shape.width;
      const p = shape.points;
      if (p.length === 1) {
        ctx.beginPath();
        ctx.arc(p[0].x, p[0].y, shape.width / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.length > 1) {
        // Линия идёт по серединам отрезков, точки — контрольные для
        // квадратичной кривой. Дрожь мыши гасится, петли остаются.
        ctx.beginPath();
        ctx.moveTo(p[0].x, p[0].y);
        for (let i = 1; i < p.length - 1; i++) {
          const mid = { x: (p[i].x + p[i + 1].x) / 2, y: (p[i].y + p[i + 1].y) / 2 };
          ctx.quadraticCurveTo(p[i].x, p[i].y, mid.x, mid.y);
        }
        ctx.lineTo(p[p.length - 1].x, p[p.length - 1].y);
        ctx.stroke();
      }
    } else if (shape.tool === "line") {
      ctx.lineWidth = PEN;
      ctx.beginPath();
      ctx.moveTo(shape.from.x, shape.from.y);
      ctx.lineTo(shape.to.x, shape.to.y);
      ctx.stroke();
    } else if (shape.tool === "ellipse") {
      ctx.lineWidth = PEN;
      ctx.beginPath();
      ctx.ellipse(
        (shape.from.x + shape.to.x) / 2,
        (shape.from.y + shape.to.y) / 2,
        Math.abs(shape.to.x - shape.from.x) / 2,
        Math.abs(shape.to.y - shape.from.y) / 2,
        0,
        0,
        Math.PI * 2,
      );
      ctx.stroke();
    } else if (shape.tool === "text") {
      ctx.font = `${TEXT_PX}px "IBM Plex Mono", ui-monospace, monospace`;
      ctx.textBaseline = "middle";
      ctx.fillText(shape.value, shape.at.x, shape.at.y);
    }
  }
  ctx.globalCompositeOperation = "source-over";
}

export function Notebook({
  visible,
  expanded,
  initialShapes,
  onChange,
}: {
  visible: boolean;
  expanded: boolean;
  initialShapes: Shape[];
  onChange: (shapes: Shape[]) => void;
}) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [tool, setTool] = useState<Tool>("pen");
  const [shapes, setShapes] = useState<Shape[]>(initialShapes);
  const [live, setLive] = useState<Shape | null>(null);
  const [typing, setTyping] = useState<{ at: Point; value: string } | null>(null);
  // Переключатель темы стоит на каждом экране, а цвет чернил берётся из --ink
  // при отрисовке: без слежения штрихи остаются цвета прошлой темы.
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme);

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(document.documentElement.dataset.theme));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => onChange(shapes), [onChange, shapes]);

  // Перерисовка целиком на каждое изменение: инкрементальная отрисовка
  // несовместима ни с отменой, ни с ластиком через destination-out.
  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const dpr = window.devicePixelRatio || 1;
    el.width = Math.round(SHEET_W * dpr);
    el.height = Math.round(SHEET_H * dpr);
    const ctx = el.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, SHEET_W, SHEET_H);
    draw(ctx, live ? [...shapes, live] : shapes, ink());
  }, [shapes, live, theme]);

  const at = (event: React.PointerEvent): Point => {
    const rect = canvas.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    // Прямоугольник холста уже учитывает прокрутку окна, поэтому координаты
    // получаются абсолютными по листу и не зависят от того, свёрнут он или нет.
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const down = (event: React.PointerEvent): void => {
    // Щелчок внутри поля ввода принадлежит полю: без этого попытка поставить
    // курсор в набранный текст закрывала ввод.
    if ((event.target as HTMLElement).tagName === "INPUT") return;
    // Фокус окну — иначе Ctrl+Z, повешенный на лист, никогда не сработает.
    (event.currentTarget as HTMLElement).focus({ preventScroll: true });

    if (typing) {
      commitText();
      return;
    }
    const point = at(event);
    if (tool === "text") {
      // Действие по умолчанию у mousedown переводит фокус — и только что
      // смонтированное поле немедленно теряло его, а onBlur закрывал ввод.
      event.preventDefault();
      setTyping({ at: point, value: "" });
      return;
    }
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    setLive(
      tool === "pen" || tool === "erase"
        ? { tool, points: [point], width: tool === "erase" ? ERASER : PEN }
        : { tool, from: point, to: point },
    );
  };

  const move = (event: React.PointerEvent): void => {
    if (!live) return;
    const point = at(event);
    setLive((current) => {
      if (!current) return current;
      if (current.tool === "pen" || current.tool === "erase") {
        const last = current.points[current.points.length - 1];
        // Точки ближе двух пикселей — дрожь, а не движение руки.
        if (Math.hypot(point.x - last.x, point.y - last.y) < 2) return current;
        return { ...current, points: [...current.points, point] };
      }
      return { ...current, to: point };
    });
  };

  const up = (): void => {
    if (!live) return;
    setShapes((s) => [...s, live]);
    setLive(null);
  };

  const commitText = (): void => {
    if (typing && typing.value.trim().length > 0) {
      setShapes((s) => [...s, { tool: "text", at: typing.at, value: typing.value }]);
    }
    setTyping(null);
  };

  return (
    <>
      <div className="chips scratch__tools">
        {TOOLS.map(([key, label]) => (
          <button
            key={key}
            className="chip scratch__icon-button"
            aria-label={label}
            title={label}
            aria-pressed={tool === key}
            onClick={() => {
              commitText();
              setTool(key);
            }}
          >
            <ScratchIcon name={key} />
          </button>
        ))}
      </div>

      <Sheet
        width={SHEET_W}
        height={SHEET_H}
        visible={visible}
        expanded={expanded}
        tabIndex={0}
        className="scratch__view--paper"
        sheetStyle={{
          // Клетка — фоном листа, а не на холсте: ластик работает через
          // destination-out и стёр бы её вместе со штрихами.
          backgroundImage:
            "linear-gradient(var(--hairline) 1px, transparent 1px), linear-gradient(90deg, var(--hairline) 1px, transparent 1px)",
          backgroundSize: `${CELL}px ${CELL}px`,
          cursor: tool === "text" ? "text" : "crosshair",
          touchAction: "none",
        }}
        onKeyDown={(event) => {
          // Отмена повешена на лист, а не на окно: на окне она отбирала Ctrl+Z
          // у поля ответа, которое стоит в той же колонке.
          if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z") {
            event.preventDefault();
            setShapes((s) => s.slice(0, -1));
          }
        }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
      >
        <canvas
          ref={canvas}
          style={{ position: "absolute", inset: 0, width: SHEET_W, height: SHEET_H }}
        />
        {typing ? (
          <input
            autoFocus
            value={typing.value}
            onChange={(event) => setTyping({ ...typing, value: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === "Enter") commitText();
              if (event.key === "Escape") setTyping(null);
            }}
            onBlur={commitText}
            style={{
              position: "absolute",
              left: typing.at.x,
              top: typing.at.y - TEXT_PX / 2 - 2,
              width: 220,
              font: `${TEXT_PX}px "IBM Plex Mono", ui-monospace, monospace`,
              color: "var(--ink)",
              background: "transparent",
              border: "none",
              borderBottom: "1px solid var(--act)",
              outline: "none",
              padding: 0,
            }}
          />
        ) : null}
      </Sheet>

      <div className="scratch__hint">
        {/* В колонке строка короче: длинная уходила в две строки и съедала лист. */}
        <span>
          {shapes.length} {plural(shapes.length, "штрих", "штриха", "штрихов")} · Ctrl+Z
          {expanded ? " отменяет · подвинуть нарисованное нельзя" : null}
        </span>
        <span style={{ flex: 1 }} />
        <button className="link link--quiet" onClick={() => setShapes((s) => s.slice(0, -1))}>
          отменить
        </button>
        <button className="link link--quiet" onClick={() => setShapes([])}>
          стереть всё
        </button>
      </div>
    </>
  );
}
